import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MerchantLayout } from "@/pages/merchant/MerchantLayout";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
    Eye,
    MousePointerClick,
    TicketPercent,
    TrendingUp,
    Plus,
    ChevronRight,
} from "lucide-react";
import { publicAssetUrl } from "@/lib/api";
import { formatInr, formatDate } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { AlertCircle, Check, Megaphone, Video as VideoIcon } from "lucide-react";
import { LaunchPromoBanner } from "@/components/promo/LaunchPromoBanner";
import { ActiveBoostsSection } from "@/components/promo/FlashCreditsWidgets";
import { getFlashCredits } from "@/lib/promo";

const CHART_COLOR = "#FF7A1A";
const CHART_COLOR_2 = "#FFC300";

export default function MerchantHome() {
    const { user } = useAuth();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [plan, setPlan] = useState(null);
    const [credits, setCredits] = useState(null);

    const refreshCredits = React.useCallback(async () => {
        try {
            const c = await getFlashCredits();
            setCredits(c);
        } catch (_) {
            setCredits(null);
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const [res, sub] = await Promise.all([
                    api.get("/me/analytics/summary"),
                    api.get("/me/subscription").catch(() => ({ data: null })),
                ]);
                setSummary(res.data);
                setPlan(sub.data);
            } catch (e) {
                toast.error("Could not load your dashboard");
            } finally {
                setLoading(false);
            }
            refreshCredits();
        })();
    }, [refreshCredits]);

    const chartData = useMemo(() => {
        return (summary?.daily_series_all_posts || []).map((p) => ({
            date: p.date?.slice(5),
            views: p.views,
            engagements: p.engagements,
        }));
    }, [summary]);

    return (
        <MerchantLayout>
            <div className="px-4 pb-6 pt-4" data-testid="merchant-home-page">
                <div className="mb-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-orange-600">
                        Namaste
                    </div>
                    <h1 className="text-2xl font-extrabold text-neutral-900">
                        {user?.business_name || "Your shop"}
                    </h1>
                </div>

                {loading ? (
                    <div className="grid grid-cols-2 gap-3">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} className="h-24 rounded-2xl" />
                        ))}
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-3" data-testid="merchant-kpis">
                            <Kpi
                                icon={<Eye size={16} />}
                                label="Views (all-time)"
                                value={fmt(summary.total_views_all_time)}
                                testid="kpi-views"
                                tone="orange"
                            />
                            <Kpi
                                icon={<MousePointerClick size={16} />}
                                label="Engagements"
                                value={fmt(summary.total_engagements_all_time)}
                                testid="kpi-engagements"
                                tone="amber"
                            />
                            <Kpi
                                icon={<TicketPercent size={16} />}
                                label="Coupon claims"
                                value={fmt(summary.coupons?.total_claims_all_time)}
                                testid="kpi-claims"
                                tone="rose"
                            />
                            <Kpi
                                icon={<TrendingUp size={16} />}
                                label="Redemptions"
                                value={fmt(summary.coupons?.total_redemptions_all_time)}
                                testid="kpi-redemptions"
                                tone="emerald"
                            />
                        </div>

                        <LaunchPromoBanner
                            onChange={() => {
                                api.get("/me/subscription").then((r) => setPlan(r.data)).catch(() => {});
                            }}
                        />

                        <ActiveBoostsSection credits={credits} onChange={refreshCredits} />

                        <YourPlanSection plan={plan} />

                        {plan?.package?.features?.video_ad_slots > 0 && (
                            <Link
                                to="/merchant/videos"
                                className="mt-4 flex items-center justify-between rounded-2xl border border-blue-200 bg-blue-50 p-3 hover:bg-blue-100"
                                data-testid="home-video-ads-tile"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                                        <VideoIcon size={16} />
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold text-neutral-900">Video Ads</div>
                                        <div className="text-[11px] text-neutral-600">
                                            Up to {plan.package.features.video_ad_slots} live videos ·{" "}
                                            {(summary?.video_ads?.live ?? 0)} active
                                        </div>
                                    </div>
                                </div>
                                <span className="text-xs font-semibold text-blue-700">Manage →</span>
                            </Link>
                        )}

                        {/* Phase 15b — self-service merchant ads */}
                        <Link
                            to="/merchant/ads"
                            className="mt-3 flex items-center justify-between rounded-2xl border border-orange-200 bg-[#FFF3E6] p-3 transition hover:bg-orange-100"
                            data-testid="merchant-nav-ads"
                        >
                            <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--brand-orange)] text-white">
                                    <Megaphone size={16} />
                                </div>
                                <div>
                                    <div className="text-sm font-bold text-neutral-900">My Advertisements</div>
                                    <div className="text-[11px] text-neutral-600">
                                        Create hero, strip &amp; video ads. Approved ads go live automatically.
                                    </div>
                                </div>
                            </div>
                            <span className="text-xs font-semibold text-[var(--brand-orange)]">Manage →</span>
                        </Link>

                        <Card className="mt-4 rounded-2xl border border-neutral-200 p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-sm font-bold text-neutral-800">
                                        Last 14 days
                                    </div>
                                    <div className="text-xs text-neutral-500">
                                        Views + engagements across your offers
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 text-[11px] text-neutral-500">
                                    <LegendDot color={CHART_COLOR} label="Views" />
                                    <LegendDot color={CHART_COLOR_2} label="Engage" />
                                </div>
                            </div>
                            <div className="mt-3 h-40 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={chartData} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="grad-v" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={CHART_COLOR} stopOpacity={0.45} />
                                                <stop offset="100%" stopColor={CHART_COLOR} stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="grad-e" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={CHART_COLOR_2} stopOpacity={0.4} />
                                                <stop offset="100%" stopColor={CHART_COLOR_2} stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid stroke="#f3f4f6" vertical={false} />
                                        <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9ca3af" />
                                        <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" allowDecimals={false} />
                                        <Tooltip
                                            contentStyle={{
                                                borderRadius: 12,
                                                border: "1px solid #e5e7eb",
                                                fontSize: 12,
                                            }}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="views"
                                            stroke={CHART_COLOR}
                                            strokeWidth={2}
                                            fill="url(#grad-v)"
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="engagements"
                                            stroke={CHART_COLOR_2}
                                            strokeWidth={2}
                                            fill="url(#grad-e)"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>

                        <div className="mt-6 flex items-center justify-between">
                            <div>
                                <div className="text-sm font-bold text-neutral-800">
                                    Top posts by views
                                </div>
                                <div className="text-xs text-neutral-500">
                                    Your best performers this month.
                                </div>
                            </div>
                            <Link to="/merchant/offers">
                                <Button
                                    variant="ghost"
                                    className="h-8 text-orange-600 hover:text-orange-700"
                                    data-testid="home-see-all-offers"
                                >
                                    See all <ChevronRight size={14} className="ml-1" />
                                </Button>
                            </Link>
                        </div>
                        {summary.top_posts_by_views?.length ? (
                            <ul className="mt-2 space-y-2" data-testid="home-top-posts">
                                {summary.top_posts_by_views.map((p) => (
                                    <li key={p.post_id}>
                                        <Link
                                            to={`/merchant/offers/${p.post_id}`}
                                            className="flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-3 hover:bg-neutral-50"
                                        >
                                            <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-neutral-100">
                                                {p.poster_image_url ? (
                                                    <img
                                                        src={publicAssetUrl(p.poster_image_url)}
                                                        alt=""
                                                        className="h-full w-full object-cover"
                                                    />
                                                ) : null}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate text-sm font-semibold text-neutral-800">
                                                    {p.title}
                                                </div>
                                                <div className="mt-0.5 text-xs text-neutral-500">
                                                    {fmt(p.views)} views
                                                </div>
                                            </div>
                                            <ChevronRight size={16} className="text-neutral-400" />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="mt-3 rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 p-6 text-center">
                                <div className="text-sm font-medium text-neutral-600">
                                    No published offers yet.
                                </div>
                                <div className="mt-1 text-xs text-neutral-500">
                                    Create your first offer to start reaching
                                    customers.
                                </div>
                                <Link to="/merchant/offers/new">
                                    <Button className="mt-4 nest-gradient text-white" data-testid="home-empty-create-btn">
                                        <Plus size={16} className="mr-2" /> Create offer
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </>
                )}
            </div>
        </MerchantLayout>
    );
}

function Kpi({ icon, label, value, testid, tone = "orange" }) {
    const tones = {
        orange: "from-orange-500 to-orange-400",
        amber: "from-amber-500 to-yellow-400",
        rose: "from-rose-500 to-pink-400",
        emerald: "from-emerald-500 to-teal-400",
    };
    return (
        <Card
            className="rounded-2xl border border-neutral-200 p-3"
            data-testid={testid}
        >
            <div
                className={`flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br ${tones[tone]} text-white`}
            >
                {icon}
            </div>
            <div className="mt-2 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
                {label}
            </div>
            <div className="mt-0.5 text-xl font-extrabold text-neutral-900">{value ?? 0}</div>
        </Card>
    );
}

const LegendDot = ({ color, label }) => (
    <div className="flex items-center gap-1">
        <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: color }}
        />
        {label}
    </div>
);

function fmt(n) {
    if (n == null) return "0";
    if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
    return String(n);
}

function YourPlanSection({ plan }) {
    const [openPlans, setOpenPlans] = React.useState(false);
    const [publicPkgs, setPublicPkgs] = React.useState(null);
    React.useEffect(() => {
        if (openPlans && !publicPkgs) {
            api.get("/packages/public").then((r) => setPublicPkgs(r.data)).catch(() => {});
        }
    }, [openPlans, publicPkgs]);

    if (!plan) return null;
    const sub = plan.subscription;
    const pkg = plan.package;
    const usage = plan.usage;

    const isExpired = sub && sub.status === "EXPIRED";
    const cap = usage?.package_cap;
    const used = usage?.package_used_this_month || 0;
    const topupAvail = usage?.topup_available || 0;
    const totalRemaining = usage?.total_remaining;
    const isLow = totalRemaining != null && totalRemaining <= 10;
    const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;

    return (
        <Card className="mt-4 rounded-2xl border border-neutral-200 p-4" data-testid="merchant-plan-card">
            {isExpired && (
                <div className="mb-2 rounded-xl bg-rose-100 px-3 py-2 text-xs font-semibold text-rose-800" data-testid="plan-expired-banner">
                    Your plan has expired. Contact us to renew.
                </div>
            )}
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">Your plan</div>
                    <div className="mt-0.5 text-lg font-extrabold text-neutral-900" data-testid="plan-package-name">
                        {pkg?.name || "No active plan"}
                    </div>
                    {sub && (
                        <div className="text-[11px] text-neutral-500">
                            Tier {pkg?.tier_order || "—"} · valid till {formatDate(sub.end_date)}
                        </div>
                    )}
                    {!sub && (
                        <div className="text-[11px] text-neutral-500">
                            You're on the free baseline (100 coupons / month).
                        </div>
                    )}
                </div>
                {isLow && (
                    <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]" data-testid="plan-low-quota">
                        <AlertCircle size={10} className="mr-1" /> Running low
                    </Badge>
                )}
            </div>

            {cap != null && (
                <div className="mt-3">
                    <div className="mb-1 flex items-center justify-between text-[11px] text-neutral-500">
                        <span>Coupons this month</span>
                        <span data-testid="plan-usage-values">{used} / {cap}{topupAvail > 0 ? ` (+${topupAvail} top-up)` : ""}</span>
                    </div>
                    <Progress value={pct} className="h-2" data-testid="plan-usage-bar" />
                </div>
            )}

            <div className="mt-3 flex gap-2">
                <Dialog open={openPlans} onOpenChange={setOpenPlans}>
                    <DialogTrigger asChild>
                        <Button variant="outline" size="sm" className="text-xs" data-testid="plan-view-plans-btn">View available plans</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md" data-testid="plan-list-dialog">
                        <DialogHeader>
                            <DialogTitle>Available plans</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-2">
                            {!publicPkgs ? (
                                <div className="text-xs text-neutral-500">Loading…</div>
                            ) : publicPkgs.map((p) => (
                                <div key={p.id} className="rounded-xl border border-neutral-200 p-3">
                                    <div className="flex items-center justify-between">
                                        <div className="font-bold">{p.name}</div>
                                        <div className="font-mono text-orange-700">{formatInr(p.monthly_price_paise)}/mo</div>
                                    </div>
                                    <div className="mt-1 text-[11px] text-neutral-500">{p.description}</div>
                                    <ul className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-[11px]">
                                        <li className="flex items-center gap-1"><Check size={11} className="text-emerald-600" /> {p.features?.max_monthly_coupons_issued ?? "∞"} coupons/mo</li>
                                        <li className="flex items-center gap-1"><Check size={11} className="text-emerald-600" /> {p.features?.max_active_posts ?? "∞"} active posts</li>
                                        {p.features?.premium_listing && <li className="flex items-center gap-1"><Check size={11} className="text-emerald-600" /> Premium listing</li>}
                                        {p.features?.priority_support && <li className="flex items-center gap-1"><Check size={11} className="text-emerald-600" /> Priority support</li>}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </DialogContent>
                </Dialog>
                <a
                    href="https://wa.me/919000000000?text=Hi%20OfferNest%2C%20I%27d%20like%20to%20upgrade%20my%20plan"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-md bg-emerald-500 px-3 text-xs font-semibold text-white hover:bg-emerald-600"
                    data-testid="plan-upgrade-btn"
                >
                    Contact us to upgrade
                </a>
            </div>
        </Card>
    );
}
